import { useEffect } from "react"
import { Platform } from "react-native"

/**
 * On mobile web, prevent iOS Safari from scrolling the document body when the
 * on-screen keyboard appears.
 *
 * Why this is needed:
 *   iOS Safari has two viewports — the layout viewport (used by `100vh` / `100%`)
 *   and the visual viewport (the visible region).  When the keyboard opens, the
 *   visual viewport shrinks but the layout viewport does not.  Safari then scrolls
 *   the document body upward to bring the focused input into the visible region.
 *   That body scroll leaves a gap below our `position: absolute; bottom: 0` UI
 *   (e.g. the ButtonCluster) and persists after the keyboard dismisses.
 *
 * The fix:
 *   1. Lock the body so the browser cannot scroll it
 *      (`overflow: hidden; position: fixed; height: 100%`).
 *   2. Track the visual viewport with the VisualViewport API and resize the React
 *      Native root container to match.  All flex / absolute layouts inside
 *      reflow naturally so the inline ChatTextInput sits above the keyboard with
 *      no app-level keyboard-avoidance code required.
 *
 * No-op on native.  Safe to call once at app startup.
 */
export function useWebViewportLock() {
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return

    const root =
      (document.getElementById("root") as HTMLElement | null) ??
      (document.body.firstElementChild as HTMLElement | null) ??
      document.body

    // 1. Lock the document so iOS Safari cannot scroll it on input focus.
    const prev = {
      htmlHeight: document.documentElement.style.height,
      htmlOverflow: document.documentElement.style.overflow,
      bodyHeight: document.body.style.height,
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      bodyOverscroll: document.body.style.overscrollBehavior,
      rootHeight: root?.style.height,
    }
    Object.assign(document.documentElement.style, { height: "100%", overflow: "hidden" })
    Object.assign(document.body.style, {
      height: "100%",
      overflow: "hidden",
      position: "fixed",
      width: "100%",
      overscrollBehavior: "none",
    })

    // 2. Size the RN root to the visual viewport so it tracks the keyboard.
    const updateRootHeight = () => {
      const h = window.visualViewport?.height ?? window.innerHeight
      if (root) root.style.height = `${h}px`
    }
    updateRootHeight()

    const viewport = window.visualViewport
    viewport?.addEventListener("resize", updateRootHeight)
    // Some browsers also emit scroll on the visual viewport when the keyboard moves.
    viewport?.addEventListener("scroll", updateRootHeight)
    window.addEventListener("orientationchange", updateRootHeight)

    // Defensive: if the document body ever does scroll (Safari can sometimes
    // bypass overflow:hidden during keyboard transitions), snap it back to 0.
    const onWindowScroll = () => window.scrollTo(0, 0)
    window.addEventListener("scroll", onWindowScroll)

    return () => {
      viewport?.removeEventListener("resize", updateRootHeight)
      viewport?.removeEventListener("scroll", updateRootHeight)
      window.removeEventListener("orientationchange", updateRootHeight)
      window.removeEventListener("scroll", onWindowScroll)
      Object.assign(document.documentElement.style, {
        height: prev.htmlHeight,
        overflow: prev.htmlOverflow,
      })
      Object.assign(document.body.style, {
        height: prev.bodyHeight,
        overflow: prev.bodyOverflow,
        position: prev.bodyPosition,
        overscrollBehavior: prev.bodyOverscroll,
      })
      if (root) root.style.height = prev.rootHeight ?? ""
    }
  }, [])
}
